import {
  Component,
  Input,
  OnInit,
  HostListener,
  Renderer2,
  ChangeDetectionStrategy
} from "@angular/core";
import { MessageType } from "../../../enums/traffic-info-enums";
import { MessageModel } from "../../../models/message.model";
import { AuthService } from "../../../services/auth.service";
import { MessageService } from "../../../services/message.service";
import { UtilityService } from "../../../services/utility.service";
import { interval, Subscription, Observable } from "rxjs";
import { startWith, switchMap } from "rxjs/operators";
import { ActivatedRoute } from "@angular/router";
import { NotifyGroupsService } from "../../../services/notify-groups.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DialogService } from "../../../services/dialog.service";

@Component({
  selector: "app-message",
  standalone: false,
  templateUrl: "./message.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./message.component.scss"]
})
export class MessageComponent implements OnInit {
  private _message: MessageModel[] = [];
  originalMessage: MessageModel | null = null;
  messageType: MessageType | null = null;
  showApp = false;
  isNewMessage = false;
  laterMessageExists: boolean = false;
  numberOfVersions: number = 1;
  currentMessage: MessageModel | null = null;

  @Input() set message(val: MessageModel[]) {
    this.laterMessageExists = false;
    if (val && val.length > 0) {
      if (val[0].id) {
        this.isNewMessage = false;
        this.messageType = MessageType[val[0].type!];
      } else {
        this.isNewMessage = true;
        if (val[0].type) {
          this.messageType = MessageType[val[0].type];
        } else {
          this.messageType = MessageType.TOT;
        }
      }

      this._message = val.sort((a, b) => this.sortByNumber(a, b));
      this.showApp = this.messageType == MessageType.TI;
      this.numberOfVersions = val.length;
      this.setCurrentMessage(this.message[this.message.length - 1]);
      this.setOriginalMessage(this.createCopy(this.currentMessage!));
      if (!this.pollingActive && this.currentMessage!.id) {
        this.pollForNewVersion();
      }
    } else {
      this._message = [];
    }

    if (
      this.pollingActive &&
      (!this._message || !this.currentMessage || !this.currentMessage.id)
    ) {
      this.pollSubscription!.unsubscribe();
    }
  }


  get message() {
    return this._message;
  }

  pollingActive: boolean = false;
  pollingInterval: number = 120000; // 2 minutes
  pollSubscription: Subscription | null = null;

  //Before exiting site (reload or X-button or similar), checks if any active changes and gives warning.
  @HostListener("window:beforeunload", ["$event"])
  beforeUnloadHandler(event) {
    /*Check if new message in new window and warn
    WARNING: Will not show in chrome if no user interaction was made in the new window.
    https://developer.mozilla.org/en-US/docs/Web/API/BeforeUnloadEvent
    */
    let idFromRoute = this.activatedRoute.snapshot.paramMap.get("id");
    if (idFromRoute && idFromRoute.includes("new_message")) {
      return false;
    }

    //Check if has changes and warn
    if (this.messageService.hasChanges()) {
      return false;
    }

    //No warning in any other cases
    return true;
  }

  constructor(
    private messageService: MessageService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private snackBar: MatSnackBar,
    private renderer: Renderer2,
    private dialogService: DialogService
  ) { }

  ngOnInit() {
    this.activatedRoute.paramMap.subscribe(paramMap => {
      // Ensure user groups are loaded for the readOnly restriction
      // Subscribe to userGroupsReady$ to know when groups are loaded
      this.authService.userGroupsReady$.subscribe(loaded => {
        if (!loaded && this.authService.getActiveAccount()) {
          this.authService.getGraphUser();
        }
      });

      var id = paramMap.get("id");
      if (id && id.includes("new_message")) {
        this.newMessage();
      } else if (id) {
        this.fetchMessage(id);
      }
    });
    this.messageService.clickOpenInNewWindow.subscribe(id => {
      localStorage.setItem(
        "currentMessage:" + id,
        JSON.stringify(this.currentMessage)
      );
    });
    this.messageService.useAsTemplate.subscribe(id => {
      this.useAsTemplate(id);
    });

    // Start with new message input fields on init.
    this.messageService.clickNewMessage.emit('');
  }

  fetchMessage(id: string) {

    this.messageService.getMessageVersions(id)
      .subscribe({
        next: (data: any) => {

          const versions = Array.isArray(data) ? data : [data];
          
          const messages = versions.map(v => {
            const msg = UtilityService.mapApiResponseToMessage(v);

            if (!UtilityService.validDate(msg.validTo!)) {
              msg.validTo = null;
            }
            if (!UtilityService.validDate(msg.validFrom!)) {
              msg.validFrom = null;
            }
            return msg;
          });
          

          this.message = messages;
        },
        error: (error) => {
          console.error('Failed to fetch message versions:', error);

          this.messageService.getMessageById(id).subscribe({
            next: (data) => {
              let message = UtilityService.mapApiResponseToMessage(data);
              this.message = [message];
            },
            error: (err) => console.error('Failed to fetch message:', err)
          });
        }
      });
  }

  newMessage() {
    let currentMessageFromLocalStorage = JSON.parse(
      localStorage.getItem("currentMessage:new_message")!
    );
    if (currentMessageFromLocalStorage) {
      this.message = [currentMessageFromLocalStorage];
      localStorage.removeItem("currentMessage:new_message");
    }
  }
  
  setMessageInNewWindow(val: MessageModel[], id: string) {
    this.currentMessage = JSON.parse(
      localStorage.getItem("currentMessage:" + id)!
    );
    if (this.currentMessage) {
      localStorage.removeItem("currentMessage:" + id);
      this.laterMessageExists = false;
      if (this.currentMessage.id) {
        this.isNewMessage = false;
        this.messageType = MessageType[this.currentMessage.type!];
      } else {
        this.isNewMessage = true;
        if (this.currentMessage.type) {
          this.messageType = MessageType[this.currentMessage.type];
        } else {
          this.messageType = MessageType.TOT;
        }
      }

      this._message = val.sort((a, b) => this.sortByNumber(a, b));
      this.showApp = this.messageType === MessageType.TI;
      this.numberOfVersions = val.length;
      this.setOriginalMessage(
        this.createCopy(
          this.message.find(x => x.ver === this.currentMessage!.ver)!
        )
      );
      if (!this.pollingActive && this.currentMessage.id) {
        this.pollForNewVersion();
      }
      if (
        this.pollingActive &&
        (!this._message || !this.currentMessage || !this.currentMessage.id)
      ) {
        this.pollSubscription!.unsubscribe();
      }
    }
  }

  onChangeMessageType(shouldShowApp) {
    this.showApp = shouldShowApp;
    if (this.showApp) {
      this.messageType = MessageType.TI;
      this.setCurrentMessage(new MessageModel());
      this.messageService.changeMessageType(MessageType[MessageType.TI]);
      this.messageService.setCheckboxState(false);
    } else {
      this.messageType = MessageType.TOT;
      this.setCurrentMessage(new MessageModel());
      this.messageService.changeMessageType(MessageType[MessageType.TOT]);
      this.messageService.setCheckboxState(false);
    }
  }

  changeVersion(newVersionToShow: number) {
    this.laterMessageExists = false;
    

    const requestedMessage = this.message.find(m => Number(m.ver) === Number(newVersionToShow));
    
    if (requestedMessage) {
      this.setCurrentMessage(requestedMessage);
      this.setOriginalMessage(this.createCopy(requestedMessage));
    } else {
      console.warn('Version', newVersionToShow, 'not found in available versions');
    }
  }

  sortByNumber(a: MessageModel, b: MessageModel) {
    return (a.ver || 0) - (b.ver || 0);
  }

  saveChanges(id: string) {
    if (this.messageService.errors.size > 0) {
      this.messageService.showErrors = true;
    } else {
      if (this.currentMessage!.info.notifyGroups.length === 0 && 
        !this.messageService.isVariousSelected && this.messageType == MessageType.TI) {
        this.showSnackbar('Välj minst en mottagare.');
        return;
      }

      if (!id) {
        this.setDefaultValues();
        this.adjustNotifyGroups();
        this.messageService.loading = true;
        if (this.messageType == MessageType.TOT) {
          this.currentMessage!.info = new MessageModel().info;
        }
        
        this.messageService.createMessage(this.currentMessage!)
          .subscribe({
            next: (httpResponse: any) => {
              const res = httpResponse.body;             
              const locationHeader = httpResponse.headers.get('Location');
              
              let createdId = res?.id || res?.Id;
              
              if (!createdId && locationHeader) {
                const matches = locationHeader.match(/\/([^\/]+)$/);
                if (matches && matches[1]) {
                  createdId = matches[1];
                }
              }
              
              if (createdId) {
                this.messageService.getMessageById(createdId).subscribe({
                  next: (fetchedData) => {
                    let response = UtilityService.mapApiResponseToMessage(fetchedData);
                    if (!UtilityService.validDate(response.validTo!)) {
                      response.validTo = null;
                    }
                    if (!UtilityService.validDate(response.validFrom!)) {
                      response.validFrom = null;
                    }

                    this.messageService.loading = false;
                    this.message = [response];
                    this.setCurrentMessage(response);
                    this.setOriginalMessage(this.createCopy(this.currentMessage!));
                    this.messageService.updateList.emit(this.currentMessage!.id!);
                    this.showSnackbar('Meddelandet har skapats');
                    this.openNewMessageForm();
                  },
                  error: (error) => {
                    console.error('Failed to fetch created message:', error);
                    this.messageService.loading = false;
                    this.showSnackbar('Meddelandet skapades men kunde inte hämtas');
                  }
                });
              } else {
                this.messageService.loading = false;
                
                this.openNewMessageForm();
                
                this.messageService.updateList.emit('');
                
                this.showSnackbar('Meddelandet har skapats');
              }
            },
            error: (err) => {
              console.error('Failed to create message. Error:', err);
              this.messageService.loading = false;
              this.showSnackbar('Misslyckades att skapa meddelande');
            }
          });
      } else {
        this.messageService.loading = true;
        this.setDefaultValues();
        this.adjustNotifyGroups();
        
        if (this.messageType == MessageType.TOT) {
          this.currentMessage!.info = new MessageModel().info;
        }
        
        this.messageService.createMessageVersion(id, this.currentMessage!)
          .subscribe({
            next: (res) => {
              this.messageService.loading = false;
              this.showSnackbar('Meddelandet har uppdaterats');
              

              this.messageService.getMessageVersions(id).subscribe({
                next: (data: any) => {
                  const versions = Array.isArray(data) ? data : [data];
                  const messages = versions.map(v => {
                    let msg = UtilityService.mapApiResponseToMessage(v);

                    if (!UtilityService.validDate(msg.validTo!)) {
                      msg.validTo = null;
                    }
                    if (!UtilityService.validDate(msg.validFrom!)) {
                      msg.validFrom = null;
                    }
                    return msg;
                  });
                  

                  this.message = messages;
                  

                  const latestVersion = messages[messages.length - 1];
                  this.setCurrentMessage(latestVersion);
                  this.setOriginalMessage(this.createCopy(latestVersion));
                  

                  this.messageService.updateList.emit(this.currentMessage!.id!);
                },
                error: (error) => {
                  console.error('Failed to fetch versions after update:', error);

                  this.messageService.getMessageById(id).subscribe({
                    next: (data) => {
                      let newVersion = UtilityService.mapApiResponseToMessage(data);
                      if (!UtilityService.validDate(newVersion.validTo!)) {
                        newVersion.validTo = null;
                      }
                      if (!UtilityService.validDate(newVersion.validFrom!)) {
                        newVersion.validFrom = null;
                      }
                      this.message = [newVersion];
                      this.setCurrentMessage(newVersion);
                      this.setOriginalMessage(this.createCopy(newVersion));
                      this.messageService.updateList.emit(this.currentMessage!.id!);
                    },
                    error: (err) => console.error(JSON.stringify(err))
                  });
                }
              });
            },
            error: (err) => {
              console.error('Failed to update message:', err);
              this.messageService.loading = false;
              this.showSnackbar('Misslyckades att uppdatera meddelande');
            }
          });

        this.messageService
          .getMessageById(this.currentMessage!.id!)
          .subscribe({
            next: (data) => {
              let message = UtilityService.mapApiResponseToMessage(data);
              if (!UtilityService.validDate(message.validTo!)) {
                message.validTo = null;
              }
              if (!UtilityService.validDate(message.validFrom!)) {
                message.validFrom = null;
              }
              
              if (message.ver > this.currentMessage!.ver) {
                this.laterMessageExists = true;
                this._message = [message].sort((a, b) => this.sortByNumber(a, b));
                this.numberOfVersions = this._message.length;
              }
              else {
                this.laterMessageExists = false;
              }
            },
            error: (error) => console.error(JSON.stringify(error))
          });
      }
    }
  }

  private adjustNotifyGroups() {
    if (this.messageType == MessageType.TI) {
      //Map group P-SV/KSV/OTS to groups P-SV/OTS, P-KSV before sending a message to BE
      NotifyGroupsService.mapAggregateGroupToIndividualGroups(this.currentMessage!.info.notifyGroups);
    }
  }

  closeMessage(id: string) {
    if (this.currentMessage && this.currentMessage.id == id) {
      // Use the dedicated close endpoint
      this.messageService
        .closeMessage(id)
        .subscribe({
          next: (res) => {
            // Refresh the message list
            this.messageService.updateList.emit(this.currentMessage!.id!);
            
            // Fetch the updated message to get the new version with closed status
            this.messageService.getMessageById(id).subscribe({
              next: (data) => {
                let response = UtilityService.mapApiResponseToMessage(data);
                
                // Update the message in the array and display
                this.setCurrentMessage(response);
                this.setOriginalMessage(this.createCopy(response));
                
                this.showSnackbar('Meddelandet har släckts');
                this.openNewMessageForm();
              },
              error: (error) => {
                console.error('Failed to fetch closed message:', error);
                this.showSnackbar('Meddelandet släcktes men kunde inte hämtas');
              }
            });
          },
          error: (error) => {
            console.error('Failed to close message:', error);
            this.showSnackbar('Misslyckades att släcka meddelandet');
          }
        });
      }
  }

  canUpdate() {
    return (
      this.currentMessage!.info.notifyGroups.length > 0 &&
      !this.messageService.loading &&
      this.currentMessage!.id &&
      !UtilityService.equals(this.originalMessage, this.currentMessage)
    );
  }

  hasChanges() {
    return (
      !this.messageService.loading &&
      !UtilityService.equals(this.originalMessage, this.currentMessage)
    );
  }

  setDefaultValues() {
    if (!this.currentMessage?.createdAt)
      this.currentMessage!.createdAt = new Date();
    this.currentMessage!.updatedAt = new Date();

    if (!this.currentMessage!.validFrom)
      this.currentMessage!.validFrom = new Date();
    this.currentMessage!.validFrom.setSeconds(0);
  }

  useAsTemplate(id) {
    this.message = [
      new MessageModel(
        this.currentMessage!.body,
        null,
        this.currentMessage!.heading,
        null,
        Object.assign({}, this.currentMessage!.info, { notifyGroups: [] }),
        this.currentMessage!.type,
        null,
        null,
        null,
        1
      )
    ];
  }

  createCopy(message: MessageModel) {
    let newMessage = JSON.parse(JSON.stringify(message));

    // Convert newMessage validFrom/To from string to Date.
    this.ensureMessageFromToDatesAreDateType(newMessage);

    return newMessage;
  }

  ensureMessageFromToDatesAreDateType(message: MessageModel) {
    // Convert message validFrom/To from string to Date.
    if (typeof (message.validFrom) == typeof ("string"))
      message.validFrom = new Date(Date.parse(message.validFrom!.toString()));
    if (typeof (message.validTo) == typeof ("string"))
      message.validTo = new Date(Date.parse(message.validTo!.toString()));
  }

  pollForNewVersion() {
    if (!this.pollingActive) {
      this.pollingActive = true;
    }
    this.pollSubscription = interval(this.pollingInterval)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.messageService.getMessageById(this.currentMessage!.id!)
        )
      )
      .subscribe({
        next: (data) => {
          let message = UtilityService.mapApiResponseToMessage(data);
          if (!UtilityService.validDate(message.validTo!)) {
            message.validTo = null;
          }
          if (!UtilityService.validDate(message.validFrom!)) {
            message.validFrom = null;
          }
          
          if (message.ver > this.currentMessage!.ver) {
            this.laterMessageExists = true;

            this.currentMessage = JSON.parse(JSON.stringify(this.currentMessage));

            // Convert this.currentMessage validFrom/To from string to Date.
            this.ensureMessageFromToDatesAreDateType(this.currentMessage!);

            this._message = [message].sort((a, b) => this.sortByNumber(a, b));
            this.numberOfVersions = this._message.length;
          } else {
            this.laterMessageExists = false;
          }
        },
        error: (error) => console.error(JSON.stringify(error))
      });
  }

  setCurrentMessage(message: MessageModel) {
    this.currentMessage = message;
    this.messageService.currentMessage = message;
  }
  
  setOriginalMessage(message: MessageModel) {
    this.originalMessage = message;
    this.messageService.originalMessage = message;
  }

  isReadOnly() {
    return !this.authService.hasReadWriteGroups();
  }
  
  getMessageContainer() {
    if (this.activatedRoute.snapshot.paramMap.has("id")) {
      return "new-window-message-container";
    }
    return "message-container";
  }
  
  showSnackbar(message: string) {
    this.snackBar.open(message, 'Stäng', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: "snackbar-container"
    });
  }
  
  openNewMessageForm() {
    this.messageService.clickNewMessage.emit('');
    const headingElement = document.getElementById('headingDiv') as HTMLTextAreaElement;
    const bodyElement = document.getElementById('bodyDiv') as HTMLTextAreaElement;
    if (headingElement) {
      this.renderer.setProperty(headingElement, 'value', '');
    }
    if (bodyElement) {
      this.renderer.setProperty(bodyElement, 'value', '');
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (!this.messageService.hasChanges()) {
      return true;
    }

    return this.dialogService.unsavedNavigationConfirmation();
  }
}
